import { DataGridPro, DataGridProProps, useGridApiRef } from "@mui/x-data-grid-pro";
import { deDE, enUS } from "@mui/x-data-grid-pro/locales";
import debounce from "lodash/debounce";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { mergeGridSettings, StoredGridSettings } from "./gridSettings";
import { useUserState } from "@/hooks/userState/useUserState";

type Props = Omit<DataGridProProps, "localeText"> & {
  /**
   * Remembers the column layout (visible columns, order, widths) and the row height per user
   * under this name, e.g. `tachographDrivers`. Must be stable and unique per table.
   */
  settingsKey?: string;
};

const SAVE_DEBOUNCE_MS = 500;

const NOTHING_STORED: { settings: StoredGridSettings | null } = { settings: null };

/** A {@link DataGridPro} with translated strings. */
export function I18nGrid({ settingsKey, ...props }: Props) {
  const { i18n } = useTranslation();
  if (settingsKey) {
    return <PersistedGrid settingsKey={settingsKey} {...props} />;
  }
  return <DataGridPro localeText={getLocalization(i18n.language)} {...props} />;
}

function PersistedGrid({
  settingsKey,
  apiRef: outerApiRef,
  initialState,
  onColumnVisibilityModelChange,
  onColumnOrderChange,
  onColumnWidthChange,
  onDensityChange,
  ...props
}: Omit<Props, "settingsKey"> & { settingsKey: string }) {
  const { i18n } = useTranslation();
  const ownApiRef = useGridApiRef();
  const apiRef = outerApiRef ?? ownApiRef;
  const { value, setValue, isReady } = useUserState(`grid:${settingsKey}`, NOTHING_STORED);

  const save = useMemo(
    () =>
      debounce(() => {
        if (!apiRef.current?.exportState) return;
        const { columns, density } = apiRef.current.exportState();
        // Every column's visibility, not just the changed ones: a column the page hides by
        // default and the user showed must come back shown.
        const visible = new Set(apiRef.current.getVisibleColumns().map(column => column.field));
        const columnVisibilityModel = Object.fromEntries(
          apiRef.current.getAllColumns().map(column => [column.field, visible.has(column.field)]),
        );
        setValue({
          settings: {
            density,
            columns: {
              columnVisibilityModel,
              orderedFields: columns?.orderedFields,
              dimensions: columns?.dimensions,
            },
          },
        });
      }, SAVE_DEBOUNCE_MS),
    [apiRef, setValue],
  );

  useEffect(() => () => save.flush(), [save]);

  const mergedInitialState = useMemo(
    () => mergeGridSettings(initialState, isReady ? value.settings : null),
    [initialState, isReady, value.settings],
  );

  return (
    <DataGridPro
      // The grid reads its initial state once; mounted again when the stored settings arrive.
      key={isReady ? "stored" : "defaults"}
      localeText={getLocalization(i18n.language)}
      {...props}
      apiRef={apiRef}
      initialState={mergedInitialState}
      onColumnVisibilityModelChange={(model, details) => {
        onColumnVisibilityModelChange?.(model, details);
        save();
      }}
      onColumnOrderChange={(params, event, details) => {
        onColumnOrderChange?.(params, event, details);
        save();
      }}
      onColumnWidthChange={(params, event, details) => {
        onColumnWidthChange?.(params, event, details);
        save();
      }}
      onDensityChange={density => {
        onDensityChange?.(density);
        save();
      }}
    />
  );
}

function getLocalization(lang: string) {
  if (lang.includes("en")) return getLocaleText(enUS);

  return getLocaleText(deDE);
}

function getLocaleText(localization: unknown) {

  // @ts-ignore
  return localization.components?.MuiDataGrid.defaultProps?.localeText;
}
